import { useCallback, useEffect, useRef, useState } from 'react';

import { QUERY_STATE } from '../constants';
import {
  datasetsDatasourcesList,
  type DatasetsDatasourcesListResponse,
} from '../openapi/operations/datasetsDatasourcesList';

import {
  DATASOURCE_LIST_PAGE_SIZE,
  DATASOURCE_POLL_INTERVAL,
  needsPolling,
} from './datasourceHarvest';

type DatasourceListState = {
  /** Stav prvního načtení; pozdější obnovení ho nemění. */
  queryState: QUERY_STATE;
  data: DatasetsDatasourcesListResponse | null;
  fetchedAt: number | null;
};

export type UseDatasourceList = DatasourceListState & {
  /** Tiché obnovení: neukáže načítání ani chybu a při neúspěchu ponechá poslední data. */
  refresh(): Promise<void>;
};

/**
 * Seznam se drží mimo sdílený resource store, který při každém načtení hlásí `WAITING`
 * a po chybě data zahodí — obojí by při pollingu blikalo nebo smazalo tabulku.
 */
export const useDatasourceList = (): UseDatasourceList => {
  const [state, setState] = useState<DatasourceListState>({
    queryState: QUERY_STATE.WAITING,
    data: null,
    fetchedAt: null,
  });

  const lastRequestRef = useRef(0);
  const lastAppliedRef = useRef(0);

  const refresh = useCallback(async () => {
    const request = ++lastRequestRef.current;
    const response = await datasetsDatasourcesList({
      query: { page_size: DATASOURCE_LIST_PAGE_SIZE },
    });

    if (request < lastAppliedRef.current) {
      return;
    }

    if (response.ok) {
      lastAppliedRef.current = request;
      setState({
        queryState: QUERY_STATE.SUCCESS,
        data: response.data,
        fetchedAt: response.fetchedAt,
      });
    } else {
      setState((prev) => ({
        ...prev,
        queryState: prev.data ? prev.queryState : QUERY_STATE.FAIL,
      }));
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Závisí na celém `state`: i neúspěšné načtení vytvoří nový objekt, a tím naplánuje další kolo.
  useEffect(() => {
    if (!state.data || !needsPolling(state.data.results)) {
      return undefined;
    }

    const timer = setTimeout(refresh, DATASOURCE_POLL_INTERVAL);
    return () => clearTimeout(timer);
  }, [state, refresh]);

  return { ...state, refresh };
};
